import React, {
  FC,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../redux/hooks';
import { getFilters } from '../../redux/slices/filters';
import { RichText } from '@sitecore-jss/sitecore-jss-react';
import { ReactComponent as CloseSVG } from '../../svg/close.svg';
import type { Filter } from '../../types/OilsFIlter.types';
import type { Oil } from '../../types/Oil.type';
import './OilCard.scss';

import BaseBtn from '../../../@shared/BaseBtn';
import LazyLoading from '../../../LazyLoading/LazyLoading';

interface OilCardProps {
  itemId: string;
  oil: Oil;
  isPerformanceActive: boolean;
  onPerformanceClick: (id: string) => void;
}

const OilCard: FC<OilCardProps> = ({
  itemId,
  oil: { name, fields },
  isPerformanceActive,
  onPerformanceClick,
}) => {
  const isCoolant = !!fields?.Coolant?.fields;

  // ----- VALUES -----
  const image =
    fields?.EngineOil?.fields?.Image?.value ??
    fields?.Coolant?.fields?.Image?.value;
  const coolantName = fields?.Coolant?.fields?.Title.value ?? DEF_TXT.value;
  const engineName = fields?.EngineOil?.fields?.Title?.value ?? DEF_TXT.value;
  const oilName = isCoolant ? coolantName : engineName;
  const choiceTag = fields?.ChoiceTag?.fields?.Title?.value ?? '';
  const oilBlend = fields?.EngineOil?.fields?.Blend ?? DEF_TXT;
  const applicationsIconList =
    fields?.EngineOil?.fields?.IconList?.map(
      (item) => item.fields['Icon Css Class'].value
    ) ?? [];
  const oilMention = fields?.EngineOil?.fields?.SyntheticMention ?? DEF_TXT;
  const oilDescription = fields?.EngineOil?.fields?.Description ?? DEF_TXT;
  const oilCode = fields?.EngineOil?.fields?.EngineOilID ?? DEF_TXT;
  const potentialChangeInterval = fields?.PotentialChangeInterval ?? DEF_TXT;
  const sump = fields?.Sump ?? DEF_TXT;
  const totalRefilling = fields?.TotalRefilling ?? DEF_TXT;
  const coolingSystem = fields?.CoolingSystem ?? DEF_TXT;

  const coolantDescription = fields?.Coolant?.fields?.Description ?? DEF_TXT;
  const coolantCode = fields?.Coolant?.fields?.CoolantID ?? DEF_TXT;
  const coolantMention = fields?.Coolant?.fields?.SyntheticMention ?? DEF_TXT;
  const showIdFirst = fields?.EngineOil?.fields?.ShowIdFirst?.value === true;

  // ----- VALUES -----

  const { t } = useTranslation();
  const filters = useAppSelector(getFilters);
  const filterIdToFilterName = useCallback(
    (filterId: string, filter: Filter) =>
      filters[filter].options.find((option) => option.id === filterId)?.name,
    [filters]
  );

  const mailToValue = useMemo(
    () =>
      `mailto:?Subject=FPT industrial Engine Fluids&Body=${encodeURIComponent(
        `${filters.country.label}: ${filterIdToFilterName(
          filters.country.value,
          'country'
        )}\n${filters.application.label}: ${filterIdToFilterName(
          filters.application.value,
          'application'
        )}\n${filters.subapplication.label}: ${filterIdToFilterName(
          filters.subapplication.value,
          'subapplication'
        )}\n${filters.series.label}: ${filterIdToFilterName(
          filters.series.value,
          'series'
        )}\n${filters.homoAndMiss.label}: ${filterIdToFilterName(
          filters.homoAndMiss.value,
          'homoAndMiss'
        )}\n\n\n${t('EngineOil')}: ${oilName}\n${t('EngineOilID')}: ${oilCode.value
        }\n${t('Blend')}: ${oilBlend.value}\n${t(
          'ChoiceTag'
        )}: ${choiceTag}\n${t('SyntheticMention')}: ${oilMention.value}\n${t(
          'Description'
        )}: ${oilDescription.value}\n\n\n${t('PotentialChangeInterval')}: ${potentialChangeInterval.value
        }\n${t('Sump')}: ${sump.value}\n${t('TotalRefilling')}: ${totalRefilling.value
        }\n${t('CoolingSystem')}: ${coolingSystem.value}\n\n\n`
      )}`,
    [filters, filterIdToFilterName]
  );

  const handleAction = useCallback(() => onPerformanceClick(itemId), [itemId]);
  const handlePerformanceClose = useCallback(
    () => onPerformanceClick(null),
    []
  );

  useEffect(() => {
    handlePerformanceClose();
  }, [itemId]);

  const ref = useRef<HTMLDivElement>();

  const _className = useMemo(
    () => `OilCard ${isPerformanceActive ? 'OilCard__with_performance' : ''}`,
    [isPerformanceActive]
  );

  // ----- left space calc + height fix -----
  const [leftPx, setLeftPx] = useState(0);
  const [initialHeight, setInitialHeight] = useState<null | number>(null);

  // ----- work around not to make cards freak out when performances windows gets open -----
  const updateSizes = () => {
    setTimeout(() => {
      setLeftPx(ref.current.getBoundingClientRect().left);
      setInitialHeight(ref.current.getBoundingClientRect().height);
    }, 500);
  };

  useEffect(() => {
    updateSizes();

    if (typeof window !== "undefined") {
      window.addEventListener('resize', updateSizes);
    }
    else {
      return
    }

    return () => {
      if (typeof window !== "undefined") {
        window.removeEventListener('resize', updateSizes);
      }
    };
  }, []);


  return (
    <div
      className={_className}
      ref={ref}
      style={!initialHeight ? {} : { height: initialHeight }}
    >
      <div>
        <div className="OilCard__image_container">
          {image ? <LazyLoading field={{ value: image }} /> : <div>noimg</div>}
        </div>

        <div className="OilCard__quality_strip">
          <RichText
            tag="span"
            field={{
              value: t(choiceTag),
            }}
          />
        </div>

        <div className="OilCard__data_container">
          <div>
            <div className="OilCard__pre_title">
              {isCoolant ? t('Coolant') : t('EngineOil')}
            </div>

            <div className="OilCard__title">
              <RichText field={{ value: showIdFirst ? oilCode.value : oilName }} />
            </div>

            {!isCoolant && (
              <div className="OilCard__quick_info">
                <RichText tag="span" field={oilBlend} />

                {applicationsIconList.length > 0 &&
                  applicationsIconList.map((icon, idx) => (
                    <span key={idx} className={`icon ${icon}`} />
                  ))}
              </div>
            )}

            <div className="OilCard__mention">
              <RichText field={isCoolant ? coolantMention : oilMention} />
            </div>

            <div className="OilCard__description">
              <RichText
                field={isCoolant ? coolantDescription : oilDescription}
              />
            </div>

            <div className="OilCard__oil_code">
              <RichText field={isCoolant ? coolantCode : showIdFirst ? { value: oilName } : oilCode} />
            </div>
          </div>

          {!isCoolant && (
            <div className="OilCard__button_container">
              <BaseBtn
                fields={{
                  label: { value: t("Perfomances") as string }
                }}
                action={handleAction}
              />
            </div>
          )}
        </div>
      </div>

      {isPerformanceActive && (
        <>
          <div className="OilCard__performance_arrow" />
          <div
            className="OilCard__performance_container"
            style={{ left: -leftPx }}
          >
            <div className="OilCard__performance_header">
              <div>
                <div>{t('TechnicalPerformances')}</div>

                <button
                  aria-label="Close performance window"
                  onClick={handlePerformanceClose}
                >
                  <CloseSVG />
                </button>
              </div>
            </div>

            <div className="OilCard__performance_service">
              <div>
                <span>{t('PotentialChangeInterval')}:</span>
                <RichText tag="span" field={potentialChangeInterval} />
              </div>
            </div>

            <div className="OilCard__performance_stats">
              <div>
                <table>
                  <tbody>
                    <tr>
                      <td>{t('Sump')}:</td>
                      <td>
                        <RichText field={sump} />
                      </td>
                    </tr>
                    <tr>
                      <td>{t('TotalRefilling')}:</td>
                      <td>
                        <RichText field={totalRefilling} />
                      </td>
                    </tr>
                    <tr>
                      <td>{t('CoolingSystem')}:</td>
                      <td>
                        <RichText field={coolingSystem} />
                      </td>
                    </tr>
                  </tbody>
                </table>

                <a
                  className="OilCard__performance_email"
                  href={mailToValue}
                  target="_blank"
                >
                  {t('SEND E-MAIL')}
                </a>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default memo(OilCard);

const DEF_TXT = { value: '-' };
